import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Building2, Check, CreditCard, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { captureReservation, confirmReservation, getPaymentConfig, getPayPalConfig } from '../../lib/api/booking'
import { ApiError } from '../../lib/api/client'
import { useBooking } from '../../store/bookingStore'
import { useAuth } from '../../store/authStore'
import { isDemoEmail } from '../../lib/demo'
import { useCalculatedPrice } from '../../lib/useCalculatedPrice'
import { getCaptchaToken } from '../../lib/recaptcha'
import { StepShell } from '../../components/layout/StepShell'
import { PriceSummary } from '../../components/layout/PriceSummary'
import { Skeleton, ErrorState } from '../../components/ui/States'
import { stepPath } from '../../lib/steps'
import { PayPalButtons, PayPalCardForm } from '../summary/PayPalCheckout'
import { WalletExpress } from '../summary/WalletButtons'
import { cn } from '../../lib/cn'
import type { BookingInfo, Locale, PayPalConfig } from '../../lib/api/types'

// Until go-live we must NEVER register a real booking on a DEPLOYED host (confirmReservation
// writes one). Localhost (→ local backend) runs the real flow; deployed hosts show a preview.
const TEST_PHASE =
  typeof window !== 'undefined' &&
  !['localhost', '127.0.0.1'].includes(window.location.hostname) &&
  import.meta.env.VITE_PAYMENTS_LIVE !== 'true'

const PREVIEW_CONFIG: PayPalConfig = { clientId: '', currency: 'EUR', intent: 'capture', environment: 'sandbox' }

// Apple/Google Pay wallets can't init on localhost (PayPal CORS-blocks their config), so skip
// them there — otherwise their failure surfaces as a global error covering the whole step
// (incl. "Paga in cassa"). PayPal + card + cash work normally on localhost.
const IS_LOCALHOST =
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)

type MethodKey = 'paypal' | 'card' | 'gpay' | 'applepay' | 'cassa'

export function PaymentStep() {
  const { t, i18n } = useTranslation()
  const locale = (i18n.language as Locale) ?? 'it'
  const navigate = useNavigate()
  const { locale: routeLocale = 'it' } = useParams()

  const cart = useBooking((s) => s.cart)
  const info = useBooking((s) => s.bookingInfo)
  const calc = useBooking((s) => s.calculatedInfo)
  const setPaymentMode = useBooking((s) => s.setPaymentMode)
  const setBookingInfo = useBooking((s) => s.setBookingInfo)
  const setConfirmation = useBooking((s) => s.setConfirmation)
  const clearStaleDates = useBooking((s) => s.clearStaleDates)
  const authUser = useAuth((s) => s.user)
  useCalculatedPrice()

  // Preview = no real charge / no real booking: the global TEST_PHASE, OR a demo/review login —
  // so App Store / Play reviewers can walk the whole funnel (incl. the payment screen) safely.
  const previewPay = TEST_PHASE || isDemoEmail(authUser?.email)

  // The stale-date check runs ONCE, when the payment page opens. It used to run on every render,
  // so a customer whose chosen arrival time passed while they were paying — a declined card and a
  // second attempt is enough — had their dates wiped and was thrown back to step one, losing the
  // whole form. Nothing is lost by waiting: if the slot really has passed, the backend refuses the
  // booking with "FromDay is invalid", so a past slot can never be booked from here.
  const staleCheckedRef = useRef(false)
  useEffect(() => {
    let stale = false
    if (!staleCheckedRef.current) {
      staleCheckedRef.current = true
      stale = clearStaleDates()
    }
    if (stale || !cart.transportId || !cart.fromDay || !cart.fromHour || !cart.toDay)
      navigate(stepPath(routeLocale, 'dates'), { replace: true })
  }, [clearStaleDates, cart.transportId, cart.fromDay, cart.fromHour, cart.toDay, navigate, routeLocale])

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['paymentConfig', cart.fromDay, cart.transportId, locale],
    queryFn: () => getPaymentConfig(cart.fromDay!, cart.transportId!),
    enabled: Boolean(cart.fromDay && cart.transportId),
  })
  const onlineMode = data?.paymentModeList.find((m) => m.type === 'ONLINE')
  const onsiteMode = data?.paymentModeList.find((m) => m.type === 'ONSITE')

  const [selected, setSelected] = useState<MethodKey | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(false)
  // PayPal is verifying the payment: not paid, not failed. Shown instead of the payment methods
  // so the customer cannot pay again while the hold is open.
  const [pendingReview, setPendingReview] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const draftRef = useRef<{ reservationCode: string; paymentId: string } | null>(null)

  // Load the PayPal config up-front (wallets render above the list, before a method is picked).
  const { data: ppCfg, isLoading: ppLoading } = useQuery({
    queryKey: ['paypalConfig'],
    queryFn: getPayPalConfig,
    enabled: !!onlineMode && !previewPay,
    staleTime: Infinity,
  })

  function selectMethod(key: MethodKey) {
    setSelected(key)
    setError(false)
    setBlocked(false)
    if (key === 'cassa') setPaymentMode(onsiteMode?.id ?? null)
    else setPaymentMode(onlineMode?.id ?? null)
  }

  function set<K extends keyof BookingInfo>(key: K, value: BookingInfo[K]) {
    setBookingInfo({ [key]: value } as Partial<BookingInfo>)
  }

  function finish(res: { code: string; title?: string; text?: string }) {
    setConfirmation({ code: res.code, title: res.title, text: res.text })
    navigate(stepPath(routeLocale, 'summary'))
  }

  // Preview/demo: complete the funnel to the confirmation screen WITHOUT charging or registering
  // anything — so reviewers can see the whole flow end-to-end.
  function completeTest() {
    finish({ code: 'GSM-TEST01', title: t('summary.testTitle'), text: t('summary.testText') })
  }

  // ── Online: createOrder (confirm → DRAFT + order id) ──────────────────────
  async function ppCreateOrder(scaCard = false): Promise<string> {
    if (!calc) throw new Error('missing calculation')
    // Online pay path (PayPal wallet, card, Apple/Google Pay express) MUST open a DRAFT that only
    // confirms after a real capture. Force the ONLINE payment mode so a stale "Paga in cassa"
    // selection can never make the backend take its onsite branch (which confirms + emails with no
    // payment). Without this, tapping cassa then an express wallet button confirmed+emailed for free.
    if (!onlineMode) throw new Error('online payment unavailable')
    setError(false)
    const __captchaToken = await getCaptchaToken('confirm')
    const res = await confirmReservation({
      cart: { ...cart, paymentModeId: onlineMode.id },
      bookingInfo: { ...info, __captchaToken },
      calculatedInfo: calc,
      paymentResult: null,
      scaCard,
    })
    if (!res.reservationCode || !res.paymentId) throw new Error('confirm failed')
    draftRef.current = { reservationCode: res.reservationCode, paymentId: res.paymentId }
    return res.paymentId
  }

  async function ppOnApprove(orderID: string) {
    const draft = draftRef.current
    if (!draft) throw new Error('no draft reservation')
    const __captchaToken = await getCaptchaToken('capture')
    try {
      const res = await captureReservation({
        reservationCode: draft.reservationCode,
        paymentId: draft.paymentId,
        paymentResult: { orderID },
        __captchaToken,
      })
      // Only show the confirmation screen when the backend actually confirmed (returns a code after a
      // COMPLETED, matching capture). Never fall back to the draft code — that would show "confirmed"
      // for a payment that wasn't really captured (e.g. a cancelled verification).
      if (!res.code) throw new Error('payment not confirmed')
      finish({ code: res.code, title: res.title, text: res.text })
    } catch (e) {
      // PayPal put the capture on a risk hold. It is NOT a failure: the money may still be taken
      // once PayPal finishes, so this must never fall through to the generic "no charge was made,
      // try again" — that message is what made a customer pay a second time.
      if (e instanceof ApiError && e.code === 'ONLINE_PAYMENT_PENDING_REVIEW') {
        setPendingReview(true)
        return
      }
      throw e
    }
  }

  // ── Onsite: confirm at cashier ────────────────────────────────────────────
  async function confirmOnsite() {
    if (!calc) return
    if (previewPay) {
      completeTest()
      return
    }
    setSubmitting(true)
    setError(false)
    try {
      const __captchaToken = await getCaptchaToken('confirm')
      const res = await confirmReservation({
        cart,
        bookingInfo: { ...info, __captchaToken },
        calculatedInfo: calc,
        paymentResult: null,
      })
      if (res.code) finish({ code: res.code, title: res.title, text: res.text })
    } catch {
      setError(true)
    } finally {
      setSubmitting(false)
    }
  }

  const payProps = {
    config: previewPay ? PREVIEW_CONFIG : (ppCfg as PayPalConfig),
    locale,
    amountCents: calc?.discountedAmount ?? 0,
    createOrder: ppCreateOrder,
    onApprove: ppOnApprove,
    onError: () => setError(true),
    preview: previewPay,
  }
  const ppReady = previewPay || (!ppLoading && !!ppCfg)

  return (
    <StepShell
      stepKey="payment"
      title={t('payment.title')}
      subtitle={t('payment.subtitle')}
      onBack={() => navigate(stepPath(routeLocale, 'info'))}
    >
      {isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : (
        <div className="space-y-6">
          {previewPay && (
            <div className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-600" />
              <div className="min-w-0">
                <p className="font-display text-sm font-bold text-amber-900">{t('summary.testTitle')}</p>
                <p className="mt-0.5 text-sm leading-snug text-amber-800">{t('summary.testText')}</p>
              </div>
            </div>
          )}
          {previewPay && (
            <button
              type="button"
              onClick={completeTest}
              disabled={!calc}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-600 px-4 py-3.5 font-display text-sm font-bold text-white transition hover:bg-brand-700 disabled:opacity-60"
            >
              <Check className="h-4 w-4" /> {t('payment.completeTest')}
            </button>
          )}
          {blocked && (
            <div className="flex items-center gap-2 rounded-2xl bg-ink-900 px-4 py-3 text-sm font-medium text-white">
              <CheckCircle2 className="h-4 w-4 flex-none" /> {t('summary.testBlocked')}
            </div>
          )}
          {pendingReview && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2.5">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-none text-amber-700" />
                <div>
                  <p className="font-semibold text-ink-900">{t('payment.reviewTitle')}</p>
                  <p className="mt-1 text-[13.5px] leading-relaxed text-ink-700">{t('payment.reviewBody')}</p>
                </div>
              </div>
            </div>
          )}
          {!pendingReview && error && <ErrorState message={t('payment.payError')} onRetry={selected === 'cassa' ? confirmOnsite : undefined} />}

          <section className="space-y-3">
            <p className="gsm-label">{t('payment.method')}</p>
            {isLoading ? (
              [0, 1].map((i) => <Skeleton key={i} className="h-16" />)
            ) : (
              <div className="space-y-3">
                {onlineMode && ppReady && !IS_LOCALHOST && <WalletExpress {...payProps} />}
                <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border-2 border-ink-100 bg-white">
                {onlineMode && (
                  <>
                    <MethodRow
                      icon={<PayPalMark />}
                      title="PayPal"
                      sub={t('payment.paypalSub')}
                      selected={selected === 'paypal'}
                      onSelect={() => selectMethod('paypal')}
                    >
                      {ppReady ? <PayPalButtons {...payProps} /> : <PanelLoader label={t('summary.pay.loading')} />}
                    </MethodRow>
                    <MethodRow
                      icon={<CreditCard className="h-5 w-5" />}
                      title={t('summary.pay.debitCredit')}
                      sub={t('payment.cardSub')}
                      selected={selected === 'card'}
                      onSelect={() => selectMethod('card')}
                    >
                      {ppReady ? <PayPalCardForm {...payProps} createOrder={() => ppCreateOrder(true)} /> : <PanelLoader label={t('summary.pay.loading')} />}
                    </MethodRow>
                  </>
                )}
                {onsiteMode && (
                  <MethodRow
                    icon={<Building2 className="h-5 w-5" />}
                    title={onsiteMode.label}
                    selected={selected === 'cassa'}
                    onSelect={() => selectMethod('cassa')}
                  >
                    <div className="space-y-3">
                      {onsiteMode.description && (
                        <div
                          className="text-sm leading-snug text-ink-500 [&_p]:m-0 [&_p+p]:mt-1"
                          dangerouslySetInnerHTML={{ __html: onsiteMode.description }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={confirmOnsite}
                        disabled={submitting || !calc}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3 font-display text-sm font-bold text-white transition hover:bg-brand-700 disabled:opacity-60"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        {t('summary.confirm')}
                      </button>
                    </div>
                  </MethodRow>
                )}
                </div>
              </div>
            )}
          </section>

          <div className="lg:hidden">
            <PriceSummary />
          </div>
        </div>
      )}
    </StepShell>
  )
}

function MethodRow({
  icon,
  title,
  sub,
  selected,
  disabled,
  onSelect,
  children,
}: {
  icon: React.ReactNode
  title: string
  sub?: string
  selected?: boolean
  disabled?: boolean
  onSelect?: () => void
  children?: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <div className={cn(disabled && 'opacity-60')}>
      <button
        type="button"
        onClick={onSelect}
        disabled={disabled}
        className={cn('flex w-full items-center gap-3 p-4 text-left transition', !disabled && 'hover:bg-ink-50/60', selected && 'bg-ink-50/60')}
      >
        <span className={cn('flex h-5 w-5 flex-none items-center justify-center rounded-full border-2 transition', selected ? 'border-brand-600' : 'border-ink-300')}>
          {selected && <span className="h-2.5 w-2.5 rounded-full bg-brand-600" />}
        </span>
        <span className="flex h-8 w-10 flex-none items-center justify-center text-ink-600">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-ink-900">{title}</span>
          {sub && <span className="block text-xs text-ink-400">{sub}</span>}
        </span>
        {disabled && <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-400">{t('payment.soon')}</span>}
      </button>
      {selected && !disabled && children && <div className="border-t border-ink-100 bg-ink-50/40 p-4">{children}</div>}
    </div>
  )
}

function PanelLoader({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-4 text-sm text-ink-400">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}
    </div>
  )
}

function PayPalMark() {
  // PayPal brand mark (FontAwesome paypal glyph), in PayPal blue.
  return (
    <svg viewBox="0 0 384 512" className="h-5 w-5" role="img" aria-label="PayPal" fill="#003087">
      <path d="M111.4 295.9c-3.5 19.2-17.4 108.7-21.5 134-.3 1.8-1 2.5-3 2.5H12.3c-7.6 0-13.1-6.6-12.1-13.9L58.8 46.6c1.5-9.6 10.1-16.9 20-16.9 152.3 0 165.1-3.7 204 11.4 60.1 23.3 65.6 79.5 44 140.3-21.5 62.6-72.5 89.5-140.1 90.3-43.4.7-69.5-7-75.3 24.2zM357.1 152c-1.8-1.3-2.5-1.8-3 1.3-2 11.4-5.1 22.5-8.8 33.6-39.9 113.8-150.5 103.9-204.5 103.9-6.1 0-10.1 3.3-10.9 9.4-22.6 140.4-27.1 169.7-27.1 169.7-1 7.1 3.5 12.9 10.6 12.9h63.5c8.6 0 15.7-6.3 17.4-14.9.7-5.4-1.1 6.1 14.4-91.3 4.6-22 14.3-19.7 29.3-19.7 71 0 126.4-28.8 142.9-112.3 6.5-34.8 4.6-65.1-13.3-89.5z" />
    </svg>
  )
}
